import { uid } from '../../../../lib/uid';

/*
  Typed item numbers (product call, 16 Oct 2026): the official number off the plantilla, typed
  on every row, unique across every item there has ever been. Shared by the New plantilla form
  and Add slots, so the two grids cannot disagree about what a valid row is.
*/

/** One row of the slots grid — one item, one typed number. */
export interface ItemNumberRow {
  key: string;
  itemNumber: string;
}

/** The server's cap on one request — stated where the reader meets it. */
export const ITEM_NUMBERS_MAX = 200;

/** The column width and character set the server holds a number to. */
export const ITEM_NUMBER_MAX_LENGTH = 64;
const ITEM_NUMBER_PATTERN = /^[A-Za-z0-9][A-Za-z0-9/._ -]*$/;

export const blankItemNumberRow = (): ItemNumberRow => ({ key: uid(), itemNumber: '' });

/** Rows from numbers — Autogenerate's, or a seed. Never none: an inline table is never empty. */
export function itemNumberRows(numbers: string[]): ItemNumberRow[] {
  return numbers.length > 0 ? numbers.map((itemNumber) => ({ key: uid(), itemNumber })) : [blankItemNumberRow()];
}

/**
 * Why a row is wrong, or null — blank, malformed, or a number another row of THIS grid already
 * carries (compared ignoring case, as the database does). Whether a number is already used
 * elsewhere in the plantilla is the server's to say, on Submit.
 */
export function itemNumberProblem(
  rows: ItemNumberRow[],
  index: number,
  /** Upper-cased numbers typed in OTHER grids of the same form — another unit's card. */
  elsewhere: ReadonlySet<string> = new Set(),
): string | null {
  const number = rows[index].itemNumber.trim();

  if (number.length === 0) return null; // Blank is "not finished yet", said by the disabled Submit.
  if (number.length > ITEM_NUMBER_MAX_LENGTH) return `At most ${ITEM_NUMBER_MAX_LENGTH} characters.`;
  if (!ITEM_NUMBER_PATTERN.test(number)) return 'Use letters, numbers, and - / . _ or spaces.';

  const upper = number.toUpperCase();
  const firstAt = rows.findIndex((row) => row.itemNumber.trim().toUpperCase() === upper);

  if (firstAt < index) return `Item number ${number} is entered twice.`;

  return elsewhere.has(upper) ? `Item number ${number} is also entered for another unit.` : null;
}

/** Every row typed, well-formed and distinct — what the server will accept in shape. */
export function itemNumbersValid(rows: ItemNumberRow[], elsewhere: ReadonlySet<string> = new Set()) {
  return (
    rows.length <= ITEM_NUMBERS_MAX &&
    rows.every(
      (row, index) => row.itemNumber.trim().length > 0 && itemNumberProblem(rows, index, elsewhere) === null,
    )
  );
}

/** The upper-cased numbers a grid holds — for the OTHER grids to check against. */
export function itemNumberSet(rows: ItemNumberRow[]) {
  return new Set(rows.map((row) => row.itemNumber.trim().toUpperCase()).filter((n) => n.length > 0));
}

/** The numbers to send, trimmed. */
export function itemNumbersOf(rows: ItemNumberRow[]) {
  return rows.map((row) => row.itemNumber.trim());
}
